/**
 * Stamp render + placement constants.
 *
 * Shared between the StampAnnotationView (rendering), the side-toolbar
 * popover (preset selection), and the modal container (default
 * placement size). Living in one tiny file keeps the magic numbers in
 * one place — bumping `STAMP_BASE_HEIGHT_NORM` here re-derives the
 * scale used by every existing stamp at the next render.
 */
/**
 * Fixed font-size of the inner stamp text. This is the modal-canonical
 * size; stream-vs-modal sizing is owned by the parent
 * `.annotations-container`'s `transform: scale(--annotation-natural-
 * scale)`, so a label stamp declared at this 24px renders at 24px in
 * the modal and ~12px on stream-card hosts — smoothly animated by the
 * GPU during the modal's FLIP grow rather than re-rasterized at
 * discrete font sizes.
 *
 * Modern browsers re-rasterize text whenever the font-size changes,
 * so picking a single fixed natural size here (and routing all size
 * variation through a single composited transform up at the
 * container) keeps glyph rasterization stable through the FLIP —
 * same approach drawings use (their wrapper carries the bbox size
 * via `transform: scale(frame.width, frame.height)`).
 */
export const STAMP_BASE_FONT_PX = 24;
/**
 * The persisted `transform.height` value that maps to a render scale
 * of exactly 1.0 (i.e. the stamp renders at its intrinsic
 * `STAMP_BASE_FONT_PX` size). Resize gestures multiply this — a
 * stamp persisted at `height = STAMP_BASE_HEIGHT_NORM * 2` renders
 * at `scale = 2`.
 */
export const STAMP_BASE_HEIGHT_NORM = 0.06;
/**
 * Default `transform.height` used when placing a fresh stamp on a
 * block. Equal to the base height so a brand-new stamp renders at
 * scale 1.0, then the user can resize from there.
 */
export const STAMP_DEFAULT_HEIGHT = 0.06;
/**
 * Five preset stamps shown in the rubber-stamp popover. Colors
 * project from the shared `INK` palette so a "yellow" stamp matches
 * the "yellow" drawing-tool swatch and "yellow" highlight, etc.
 * The 5th preset uses a different hue (purple) deliberately — it's
 * the joke one and should stand apart.
 */
import { INK } from "../palette";
export const STAMP_PRESETS = [
    { text: "WIP", color: INK.yellow.hex },
    { text: "TODO", color: INK.blue.hex },
    { text: "DONE", color: INK.green.hex },
    { text: "VOID", color: INK.red.hex },
    { text: "BOOOO", color: INK.purple.hex },
];
